<template>
  <el-col :span="24" id="list-view" v-loading.body="changingPage && !navigating" element-loading-text="Loading...">
    <ListViewHeader
      title="ACCOUNTS RECEIVABLE"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="'NewAr'"
    />
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="3"
        :autoARply="false"
        clearAction="clearArState"
        clearMetadataAction="resetArMeta"
        searchAction="fetchArsWithQuery"
        transactionType="Ar"
        @filterCleared="() => $refs.filters.ARplyFilters()"
        @doneFiltering="getARpliedFilters"
        :lineFieldSearch="otherNameFieldEnabled ? { field: 'other_name', transactionType: 'ar' } : null">
        <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="props" :key="i">
          <component :is="f.type" :properties="f.props" :ref="el => collectFilterRef(`${f.type}_ref`, el, i)"/>
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col>
        <el-row type="flex">
          <el-table
            v-if="meta.count && !changingPage"
            id="ars-table"
            ref="arsTable"
            :data="ars"
            border
            style="width: 100%; z-index: 0;"
            :maxHeight="tableHeight"
            :default-sort="{ prop: 'ledger', order: 'descending' }"
            @select="handleSelectionChange"
            @select-all="handleSelectionChange">
            <el-table-column type="selection" width="55" align="center"></el-table-column>
            <el-table-column prop="ledger" label="ID" align="center" width="100">
              <template v-slot="scope">
                <el-row type="flex" justify="center">
                  <router-link :to="{name: 'EditAr', params: { id: scope.row.id }}">
                    {{ scope.row.ledger }}
                  </router-link>
                </el-row>
              </template>
            </el-table-column>
            <el-table-column prop="number" label="Number" align="left" width="200">
              <template v-slot="scope">
                <el-row type="flex" justify="start">
                  <router-link :to="{name: 'EditAr', params: { id: scope.row.id }}">
                    {{ scope.row.number }}
                  </router-link>
                </el-row>
              </template>
            </el-table-column>
            <el-table-column prop="customer" label="Customer" align="left" width="200">
              <template v-slot="scope">
                {{
                  getCustomerById(scope.row.customer) && getCustomerById(scope.row.customer).name
                    ? getCustomerById(scope.row.customer).name
                    : ''
                }}
              </template>
            </el-table-column>
            <el-table-column prop="description" label="Description" align="left" fit />
            <el-table-column prop="amount_decimal" label="Amount" align="right" width="140">
              <template v-slot="scope">
                {{ formatAmount(scope.row.amount_decimal) }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="Status" width="180" align="center">
              <template v-slot="scope">
                <el-row type="flex" justify="center">
                  <el-col v-if="['OPEN', 'AUDITED'].includes(scope.row.status)">
                    <el-button-group>
                      <el-button size="small" plain disabled>
                        {{ getBidOrBillStatus(scope.row.status) }}
                      </el-button>
                      <el-button
                        type="info"
                        size="small"
                        :disabled="printingOnly || disableAuditBtn || disablePostBtn"
                        :loading="disableAuditBtn === scope.row.id"
                        @click="handleAuditBtnClick(scope.row, 'Accounts Receivable')">
                        {{ disableAuditBtn === scope.row.id ? '' : 'Audit' }}
                      </el-button>
                      <el-button
                        type="info"
                        size="small"
                        :disabled="printingOnly || disableAuditBtn || disablePostBtn"
                        :loading="disablePostBtn === scope.row.id"
                        @click="handlePostBtnClick(scope.row)">
                        {{ disablePostBtn === scope.row.id ? '' : 'Post' }}
                      </el-button>
                    </el-button-group>
                  </el-col>
                  <el-col v-else-if="scope.row.status === 'DRAFT'">
                    <el-tag type="primary">
                      {{ getBidOrBillStatus(scope.row.status) }}
                    </el-tag>
                  </el-col>
                  <el-col v-else-if="scope.row.status === 'POSTED'">
                    <el-button-group>
                      <el-button size="small" plain disabled>
                        {{ getBidOrBillStatus(scope.row.status) }}
                      </el-button>
                      <el-button
                        type="info"
                        size="small"
                        :disabled="printingOnly || disableAuditBtn || disablePostBtn"
                        :loading="printingOnly === scope.row.id"
                        @click="handlePostBtnClick(scope.row)">
                        {{ printingOnly === scope.row.id ? '' : 'Print' }}
                      </el-button>
                    </el-button-group>
                  </el-col>
                  <el-col v-else>
                    <el-tag type="info">
                      {{ getBidOrBillStatus(scope.row.status) }}
                    </el-tag>
                  </el-col>
                </el-row>
              </template>
            </el-table-column>
          </el-table>
        </el-row>
        <el-row v-if="!meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import { filterRefsMixin } from '@/Mixins/FilterRefs'
  import store from '@/store/store'
  import { payload as arActionPayload } from '@/components/RouteComponents/Ars/actionPayload'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      ListViewHeader
    },
    mixins: [ listViewMixins, filterRefsMixin ],
    data () {
      return {
        filters: [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'status',
              clearable: true,
              opts: [
                { label: 'DRAFT', value: 'DRAFT' },
                { label: 'OPEN', value: 'OPEN' },
                { label: 'AUDITED', value: 'AUDITED' },
                { label: 'POSTED', value: 'POSTED' },
                { label: 'CLOSED', value: 'CLOSED' },
                { label: 'VOIDED', value: 'VOIDED' },
                { label: 'DELETED', value: 'DELETED' }
              ],
              multi: true,
              placeholder: 'Status'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'vendor',
              remote: true,
              clearable: true,
              action: 'fetchVendorsWithQuery',
              clearAction: 'clearVendorState',
              getter: 'getVendorsBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Customer',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          }
        ],
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        disablePostBtn: null,
        printingOnly: null,
        currentPage: 1
      }
    },
    methods: {
      getAppliedFilters () {
        const filters = this.$refs.filters.getFilters
        this.$store.commit('setAppliedFilters', filters)
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      /**
       * Handles paginating between AR pages
       * @param page  Target page
       */
      async changePage (page) {
        await this.$store.dispatch('setArFilterInfo', { pageCount: page })
        let filterStoreData = this.getArFilterInfo
        if (Object.keys(filterStoreData).length) {
          this.filterRetained()
          return
        }
        this.changingPage = true
        this.currentPage = page
        const { currentFilters } = this
        try {
          arActionPayload.page = page
          if (currentFilters) {
            arActionPayload.query = currentFilters
          }
          await this.$store.dispatch('clearArState', [])
          const entities = await store.dispatch('fetchByPage', arActionPayload)
          if (entities) {
            const dependencies = await store.dispatch('findDependencies', entities)
            await Promise.all(dependencies)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.restoreSelection('arsTable')
        } catch (e) {
          this.changingPage = false
          console.log(e)
        }
      },
      async filterRetained () {
        this.changingPage = true
        // Get FilterInfo from the store data
        let filterStoreData = this.getArFilterInfo
        if (!Object.keys(filterStoreData).length) {
          const query = { status__in: [ 'OPEN', 'AUDITED', 'DRAFT', 'POSTED', 'CLOSED', 'VOIDED' ], exclude_duplicate_voids: 'True' }
          const entities = await store.dispatch('fetchArsWithQuery', { query, storeLines: false })
          await store.dispatch('setFilter', query)
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          this.changingPage = false
          return
        }
        try {
          const { currentFilters } = this
          // Prepare payload query to ARpend to the ARI
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page: filterStoreData['pageCount'] ? filterStoreData['pageCount'] : this.currentPage }, storeLines: false })
            : Object.assign({}, { query: { page: filterStoreData['pageCount'] ? filterStoreData['pageCount'] : this.currentPage }, storeLines: false })
          // Check 'keyword' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
            payload.query['search'] = filterStoreData['keyword']
          }
          // Check 'status__in' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
            payload.query['status__in'] = filterStoreData['status__in']
          }
          // Check 'batch' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch'] && filterStoreData['batch'].length > 0) {
            payload.query['batch'] = filterStoreData['batch']
          }
          // Check 'vendor' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['vendor'] && filterStoreData['vendor'].length > 0) {
            payload.query['vendor'] = filterStoreData['vendor']
          }
          // Reset AR data
          await Promise.all([
            store.dispatch('resetArMeta'),
            store.dispatch('clearArState', true),
            store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
            store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
          ])
          // Get AR's data based on the filter query
          const entities = await store.dispatch('fetchArsWithQuery', payload)
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          // Prefill the data for the filters
          if (this.filters && this.filters.length && this.$refs) {
            this.$nextTick(() => {
              // Check 'keywordFilter_ar_ref' is exist or not, if exists then assing the value to prefill it
              if (this.getFilterRefs('keywordFilter_ref').length && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
                this.setFilterRefProp('keywordFilter_ref', 0, 'keyword', filterStoreData['keyword'])
                this.getFilterRef('keywordFilter_ref', 0)?.change()
              }
              // Check 'selectFilter_ar_ref' is exist or not, if exists then assing the value to prefill it
              if (this.getFilterRefs('selectFilter_ref').length && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
                this.setFilterRefProp('selectFilter_ref', 0, 'selectedVal', filterStoreData['status__in'])
                this.getFilterRef('selectFilter_ref', 0)?.change()
              }
              // Check 'autocompleteFilter_ref' is exist or not, if exists then assing the value to prefill it
              if (this.getFilterRefs('autocompleteFilter_po_ref').length && filterStoreData && ((filterStoreData['batch'] && filterStoreData['batch'].length > 0) || (filterStoreData['vendor'] && filterStoreData['vendor'].length > 0))) {
                // Check 'batch' data is exist or not, if exists then assing the value to prefill it
                if (filterStoreData && filterStoreData['batch']) {
                  let batchInfo = this.getBatchById([filterStoreData['batch']])
                  this.setFilterRefProp('autocompleteFilter_ref', 0, 'keyword', batchInfo.name)
                  this.getFilterRef('autocompleteFilter_ref', 0)?.handleSelect({label: batchInfo.name, value: batchInfo.id})
                }
                // Check 'vendor' data is exist or not, if exists then assing the value to prefill it
                if (filterStoreData && filterStoreData['vendor']) {
                  let vendorInfo = this.getVendorById([filterStoreData['vendor']])
                  this.setFilterRefProp('autocompleteFilter_ref', 1, 'keyword', vendorInfo.name)
                  this.getFilterRef('autocompleteFilter_ref', 1)?.handleSelect({label: vendorInfo.name, value: vendorInfo.id})
                }
              }
            })
          }
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      },
      /**
       * Returns the proper accounting term for an AR entry depending on its status
       * @param status
       * @return {string}
       */
      getBidOrBillStatus (status) {
        switch (status) {
          case 'OPEN':
          case 'AUDITED':
            return 'Bid'
          case 'POSTED':
          case 'CLOSED':
            return 'Bill'
        }
        return status
      }
    },
    computed: {
      ...mapGetters({
        meta: 'getArMetadata',
        getARById: 'getArById',
        getCustomerById: 'getCustomerById',
        selection: 'getSelectedTransactions',
        getArFilterInfo: 'getArFilterInfo',
        getVendorById: 'getVendorById',
        getBatchById: 'getBatchById'
      }),
      ars () {
        return this.$store.getters.getArs.filter(ar => ar.page === this.currentPage)
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('list-view').offsetHeight
        const headerHeight = document.getElementById('list-view-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    /**
     * Checks for page 1 of data. If those results exist, great! The getter should grab them when the route resolves.
     * If they don't exist, fetches them, finds dependencies, and resolves route nav.
     * @param to
     * @param from
     * @param next
     * @return {Promise.<*>}
     */
    async beforeRouteEnter (to, from, next) {
      try {
        if (!(from.name === 'EditAr' && to.name === 'ARs')) {
          await Promise.all([store.dispatch('clearSetArFilterInfo')])
        }
        if (from.name === 'EditAr' && to.name === 'ARs') {
          const query = { status__in: [ 'OPEN', 'AUDITED', 'DRAFT', 'POSTED', 'CLOSED', 'VOIDED' ], exclude_duplicate_voids: 'True' }
          await store.dispatch('setFilter', query)
          return
        }
        await Promise.all([
          store.dispatch('resetArMeta'),
          store.dispatch('clearArState', true),
          store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
          store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
        ])
      } catch (e) {
        console.log(e)
      } finally {
        // Call filterRetained directly as a standalone function
        next(vm => {
          vm.filterRetained()
        })
      }
    },
    beforeUnmount () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
    }
  }
</script>

<style scoped>
  #list-view-header {
    border-bottom: 1px solid lightgray;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>
